import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBookOpen, FiSearch, FiUser, FiLogOut, FiChevronDown, FiArrowRight } from 'react-icons/fi';
import { commonCoreProjects, newCommonCoreProjects, piscineDays, outerCircleProjects } from '../data/projects';
import './Navbar.css';

const Navbar = ({ onLogout, user }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const searchRef = useRef(null);
    const navigate = useNavigate();

    // Close search dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearchDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.trim().length > 0) {
            const query = value.toLowerCase();
            const allProjects = [
                ...newCommonCoreProjects.map(p => ({...p, type: 'New Core'})), 
                ...commonCoreProjects.map(p => ({...p, type: 'Old Core'})),
                ...piscineDays.map(p => ({...p, type: 'Piscine'})),
                ...outerCircleProjects.map(p => ({...p, type: 'Outer Circle'}))
            ];
            
            // Filter and limit results
            const filtered = allProjects.filter(p => 
                p.title.toLowerCase().includes(query) || 
                p.id.toLowerCase().includes(query)
            ).slice(0, 8);
            
            setSearchResults(filtered);
            setShowSearchDropdown(true);
        } else {
            setSearchResults([]);
            setShowSearchDropdown(false);
        }
    };

    const handleProjectClick = (project) => {
        navigate(`/project/${encodeURIComponent(project.title)}`);
        setSearchTerm('');
        setShowSearchDropdown(false);
    };

    return (
        <nav className="navbar">
            <div className="navbar-logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
                <span className="logo-icon"><FiBookOpen size={24} /></span>
                <h1>Mintra<span className="highlight">Lib</span></h1>
            </div>
            <div className="navbar-search" ref={searchRef}>
                <input 
                    type="text" 
                    placeholder="Search for projects, resources..." 
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => searchTerm.trim().length > 0 && setShowSearchDropdown(true)}
                />
                <button><FiSearch size={18} /></button>

                {showSearchDropdown && searchResults.length > 0 && (
                    <div className="search-dropdown">
                        {searchResults.map((project, idx) => (
                            <div 
                                key={idx} 
                                className="search-result-item"
                                onClick={() => handleProjectClick(project)}
                            >
                                <div className="result-info">
                                    <span className="result-title">{project.title}</span>
                                    <span className="result-type">{project.type} • {project.rank}</span>
                                </div>
                                <FiArrowRight className="result-arrow" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="navbar-actions">
                <div 
                    className={`user-profile ${isDropdownOpen ? 'active' : ''}`} 
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    {user && user.image_url ? (
                        <img src={user.image_url} alt="Profile" className="avatar-img" style={{width: 32, height: 32, borderRadius: '50%'}} />
                    ) : (
                        <div className="avatar"><FiUser /></div>
                    )}
                    <span className="profile-name">{user ? user.login : 'Guest'}</span>
                    <FiChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`} />
                    
                    {isDropdownOpen && (
                        <div className="profile-dropdown">
                            <button className="dropdown-item logout-btn" onClick={(e) => {
                                e.stopPropagation();
                                onLogout();
                            }}>
                                <FiLogOut size={16} /> 
                                <span>Logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};


export default Navbar;
